import { Text, JWTRole } from "@hackthenorth/north";
import { Menu } from "@headlessui/react";
import { Button, Modal } from "north.js";
import React, { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import tw, { styled, theme } from "twin.macro";

import { useSidebarContent } from "src/shared/components/Sidebar/useSidebarContent";
import { BaseRoute, Route } from "src/shared/constants/route";
import { useUserContext } from "src/shared/contexts";
import "styled-components/macro";

type FloatingMobileNavMenuProps = {
  children: React.ReactNode;
};

const FloatingMobileNavMenu: React.FC<FloatingMobileNavMenuProps> = ({
  children,
}) => {
  const { sections } = useSidebarContent();
  const { logOut, roles } = useUserContext();
  const navigate = useNavigate();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const closeModal = () => setShowConfirmationModal(false);

  const items = sections
    .map((section) =>
      section.content.map((page) => {
        const isExternal = page.href.startsWith("http");
        const isCurrent =
          !isExternal &&
          (page.href === "/"
            ? window.location.pathname === page.href
            : window.location.pathname.startsWith(page.href));
        const Icon = isCurrent ? page.activeIcon : page.icon;

        return {
          content: (
            <SidebarItem current={isCurrent}>
              <Icon style={{ height: 20, width: 20, marginRight: 8 }} />
              <span tw="font-bold">{page.name}</span>
            </SidebarItem>
          ),
          action: () => {
            if (isExternal) {
              window.open(page.href, "_blank", "noopener,noreferrer");
              return;
            }
            navigate(page.href);
          },
        };
      })
    )
    .concat([
      [
        {
          content: <Text>Manage account</Text>,
          action: () => {
            navigate(BaseRoute.ACCOUNT);
          },
        },
      ],
      ...(!roles.includes(JWTRole.SPONSOR) && !roles.includes(JWTRole.VOLUNTEER)
        ? [
            [
              {
                content: <Text>Settings</Text>,
                action: () => {
                  navigate(BaseRoute.SETTINGS);
                },
              },
            ],
          ]
        : []),
      ...(roles.includes(JWTRole.VOLUNTEER)
        ? [
            [
              {
                content: <Text>Profile</Text>,
                action: () => {
                  navigate(Route.VOLUNTEER_PROFILE);
                },
              },
            ],
          ]
        : []),
      [
        {
          content: <Text>Log out</Text>,
          action: () => {
            setShowConfirmationModal(true);
          },
        },
      ],
    ]);

  return (
    <>
      <Modal
        open={showConfirmationModal}
        onClose={closeModal}
        style={{ width: "90vw", position: "fixed", top: "25vh", left: "5vw" }}
      >
        <Modal.Header>Log out</Modal.Header>
        <Modal.Body tw="w-full">Are you sure you want to log out?</Modal.Body>
        <Modal.Footer justify="end">
          <Button size="md" color="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            size="md"
            color="primary"
            onClick={() => {
              logOut();
              closeModal();
            }}
          >
            Log out
          </Button>
        </Modal.Footer>
      </Modal>
      <div tw="relative inline-block text-left z-50">
        <Menu>
          <Menu.Button aria-label="Open navigation" tw="block">
            {children}
          </Menu.Button>
          <ScrollableMenu>
            {items.map((section, sectionIndex) => (
              <div key={sectionIndex} tw="py-4">
                {section.map((item, itemIndex) => (
                  <Menu.Item key={itemIndex}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={item.action}
                        tw="flex w-full items-center justify-between px-16 py-8 text-12"
                        css={
                          active
                            ? tw`bg-background-primary text-white`
                            : tw`text-gray-800`
                        }
                      >
                        {item.content}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            ))}
          </ScrollableMenu>
        </Menu>
      </div>
    </>
  );
};

const MenuPanel = styled.div`
  ${tw`absolute left-0 mt-8 origin-top-left divide-y divide-gray-100 bg-white border border-gray-300 shadow-lg focus:outline-none`}
  width: 224px;
  border-radius: 6px;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  max-width: calc(100vw - 32px);
`;

const ScrollableMenu: React.FC = ({ children }) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    // TypeScript 3.8's DOM types predate VisualViewport.
    const viewport = (
      window as Window & {
        visualViewport?: EventTarget & { height: number; offsetTop: number };
      }
    ).visualViewport;
    const updateHeight = () => {
      const bottom = viewport
        ? viewport.height + viewport.offsetTop
        : window.innerHeight;
      panel.style.maxHeight = `${Math.max(
        0,
        bottom - panel.getBoundingClientRect().top - 16
      )}px`;
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    window.addEventListener("scroll", updateHeight);
    viewport?.addEventListener("resize", updateHeight);
    viewport?.addEventListener("scroll", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("scroll", updateHeight);
      viewport?.removeEventListener("resize", updateHeight);
      viewport?.removeEventListener("scroll", updateHeight);
    };
  }, []);

  return (
    <Menu.Items as={MenuPanel} ref={panelRef}>
      {children}
    </Menu.Items>
  );
};

const SidebarItem = styled.div<{ current: boolean }>`
  ${tw`flex items-start w-full`}
  color: ${(props) =>
    props.current ? theme`colors.text.primary` : theme`colors.text.secondary`};

  &:hover {
    color: unset;
    text-decoration: none;
  }
`;

export default FloatingMobileNavMenu;
